import { useIntersectionObserver } from '../../hooks/useIntersectionObserver';

/**
 * Combined "Three Pillars" + "Core Competencies & Facilitator Paradigm" sections.
 *
 * Ports the vanilla HTML sections (id="pillars" and id="competencies") to React.
 *
 * Pillars section:
 *   - Animated triangle SVG linking Science / Technology / Humanity vertices.
 *   - Three pillar cards: Fully Active Learning, Google Gemini, Human Connection.
 *
 * Competencies section:
 *   - Four competency cards: Critical Thinking, Creative Thinking,
 *     Effective Communication, Effective Interaction.
 *   - Facilitator Paradigm callout comparing traditional vs. our model.
 *
 * A single IntersectionObserver on the pillars <section> drives all fade-up
 * reveals and the triangle SVG draw-on animation.
 */
export function PillarsSection() {
  const [sectionRef, isVisible] = useIntersectionObserver<HTMLElement>({ threshold: 0.1 });

  const reveal = () => `fade-up ${isVisible ? 'revealed' : ''}`;

  const stagger = (i: number): React.CSSProperties =>
    ({ '--stagger': i }) as React.CSSProperties;

  return (
    <>
      {/* ── Pillars Section ── */}
      <section
        id="pillars"
        className="section pillars-section"
        aria-label="The Three Pillars"
        ref={sectionRef}
      >
        <div className="section-inner">
          <h2 className={`section-title ${reveal()}`}>The Three Pillars</h2>
          <p className={`section-subtitle ${reveal()}`} style={stagger(1)}>
            No competitor combines all three. That's why they fail.
          </p>

          {/* Triangle SVG */}
          <div className={`pillar-triangle-wrapper ${reveal()}`} style={stagger(2)}>
            <svg
              className="pillar-triangle"
              viewBox="0 0 400 350"
              aria-label="Three pillars triangle: Science, Technology, Humanity"
            >
              <polygon
                className={`pillar-triangle-path ${isVisible ? 'animated' : ''}`}
                points="200,30 370,310 30,310"
                fill="none"
                stroke="var(--md-sys-color-primary)"
                strokeWidth="2.5"
                strokeLinejoin="round"
              />
              <text x="200" y="20" textAnchor="middle" className="pillar-triangle-label">
                Science
              </text>
              <text x="385" y="335" textAnchor="end" className="pillar-triangle-label">
                Technology
              </text>
              <text x="15" y="335" textAnchor="start" className="pillar-triangle-label">
                Humanity
              </text>
            </svg>
          </div>

          {/* Pillar Cards */}
          <div className="pillar-grid" data-stagger>
            {/* Card 1 — Fully Active Learning */}
            <div className={`pillar-card ${reveal()}`}>
              <md-elevation />
              <div className="pillar-card-header">
                <span className="material-symbols-outlined pillar-card-icon pillar-icon-science">
                  school
                </span>
                <h3>Fully Active Learning</h3>
                <span className="pillar-badge">The Methodology</span>
              </div>
              <p className="pillar-card-desc">
                100% engagement, zero passive lectures. Pre-work before every session, application
                during. Every minute is active learning. Not watching, not listening. Doing.
              </p>
              <div className="pillar-stat">
                <span className="pillar-stat-value">1.5x</span>
                <span className="pillar-stat-label">
                  more likely to fail under traditional lecturing vs. active learning
                </span>
                <cite>
                  <a
                    href="https://doi.org/10.1073/pnas.1319030111"
                    target="_blank"
                    rel="noopener"
                  >
                    Freeman et al., 2014
                  </a>
                  , PNAS meta-analysis of 225 studies
                </cite>
              </div>
            </div>

            {/* Card 2 — Google Gemini */}
            <div className={`pillar-card ${reveal()}`}>
              <md-elevation />
              <div className="pillar-card-header">
                <span className="material-symbols-outlined pillar-card-icon pillar-icon-tech">
                  auto_awesome
                </span>
                <h3>Google Gemini</h3>
                <span className="pillar-badge">The Technology</span>
              </div>
              <p className="pillar-card-desc">
                Purpose-built Socratic AI with 1M token context window, multi-step reasoning, and
                real-time code comprehension. AI that prevents outsourcing of thought, not enables it.
              </p>
              <div className="pillar-stat">
                <span className="pillar-stat-value">1M</span>
                <span className="pillar-stat-label">
                  token context window that understands entire codebases
                </span>
              </div>
            </div>

            {/* Card 3 — Human Connection */}
            <div className={`pillar-card ${reveal()}`}>
              <md-elevation />
              <div className="pillar-card-header">
                <span className="material-symbols-outlined pillar-card-icon pillar-icon-human">
                  diversity_3
                </span>
                <h3>Human Connection</h3>
                <span className="pillar-badge">The Irreplaceable</span>
              </div>
              <p className="pillar-card-desc">
                Facilitators, not lecturers. Teaching assistants, peer collaboration, and 1:1 live
                sessions. Humans create the conditions for deep cognitive engagement that no AI can
                replicate.
              </p>
              <div className="pillar-stat">
                <span className="pillar-stat-value">2 sigma</span>
                <span className="pillar-stat-label">
                  advantage: 1-on-1 tutoring outperforms lectures by two standard deviations
                </span>
                <cite>
                  <a
                    href="https://doi.org/10.3102/0013189X013006004"
                    target="_blank"
                    rel="noopener"
                  >
                    Bloom, 1984
                  </a>
                  , the 2-sigma problem; effect confirmed at d=0.79 by{' '}
                  <a
                    href="https://doi.org/10.1016/j.edrev.2011.02.002"
                    target="_blank"
                    rel="noopener"
                  >
                    VanLehn, 2011
                  </a>
                </cite>
              </div>
            </div>
          </div>

          <p className={`pillar-manifesto ${reveal()}`}>
            We don't just teach subjects. We teach people how to think.
            <br />
            <strong>AI should make you smarter, not more dependent.</strong>
          </p>
        </div>
      </section>

      {/* ── Competencies Section ── */}
      <section
        id="competencies"
        className="section competencies-section"
        aria-label="What We Build In Learners"
      >
        <div className="section-inner">
          <h2 className={`section-title ${reveal()}`}>What We Build In Learners</h2>
          <p className={`section-subtitle ${reveal()}`} style={stagger(1)}>
            Four core competencies, practiced through novel mechanics, reinforced by every layer of
            the platform.
          </p>

          <div className="competency-grid" data-stagger>
            {/* Critical Thinking */}
            <div className={`competency-card ${reveal()}`}>
              <md-elevation />
              <div className="competency-card-header">
                <span className="material-symbols-outlined competency-icon competency-icon-critical">
                  neurology
                </span>
                <h3>Critical Thinking</h3>
                <span className="competency-badge competency-badge-critical">Core Competency</span>
              </div>
              <p className="competency-card-desc">
                Systematically evaluate evidence, identify hidden assumptions, and construct
                defensible arguments under pressure.
              </p>
              <div className="competency-mechanics">
                <div className="mechanic-item">
                  <strong>Assumption Autopsy</strong>
                  <p>
                    List 3 assumptions before solving. Scout challenges the weakest with a
                    counterexample.
                  </p>
                </div>
                <div className="mechanic-item">
                  <strong>Evidence Weigh Station</strong>
                  <p>
                    Rank conflicting data sources by reliability. Explain your reasoning before the
                    answer is revealed.
                  </p>
                </div>
                <p className="mechanic-integration">
                  Reinforced by: Reverse Turing Protocol + Hypothesis Lock
                </p>
              </div>
            </div>

            {/* Creative Thinking */}
            <div className={`competency-card ${reveal()}`}>
              <md-elevation />
              <div className="competency-card-header">
                <span className="material-symbols-outlined competency-icon competency-icon-creative">
                  lightbulb
                </span>
                <h3>Creative Thinking</h3>
                <span className="competency-badge competency-badge-creative">Core Competency</span>
              </div>
              <p className="competency-card-desc">
                Generate novel solutions under constraints. Transfer concepts across domains. Solve
                the same problem three different ways.
              </p>
              <div className="competency-mechanics">
                <div className="mechanic-item">
                  <strong>Constraint Forge</strong>
                  <p>
                    Same problem, 3 solutions, each under a different constraint: no loops, no
                    objects, in 3 lines.
                  </p>
                </div>
                <div className="mechanic-item">
                  <strong>Cross-Pollination Challenge</strong>
                  <p>
                    Apply a concept from one domain to solve a problem in another. Recursion meets
                    cooking; closures meet architecture.
                  </p>
                </div>
                <p className="mechanic-integration">
                  Reinforced by: Cross-Domain Transfer + Knowledge Garden
                </p>
              </div>
            </div>

            {/* Effective Communication */}
            <div className={`competency-card ${reveal()}`}>
              <md-elevation />
              <div className="competency-card-header">
                <span className="material-symbols-outlined competency-icon competency-icon-comm">
                  record_voice_over
                </span>
                <h3>Effective Communication</h3>
                <span className="competency-badge competency-badge-comm">Core Competency</span>
              </div>
              <p className="competency-card-desc">
                Articulate complex ideas clearly. Teach what you know. If you can't explain it
                simply, you don't understand it deeply enough.
              </p>
              <div className="competency-mechanics">
                <div className="mechanic-item">
                  <strong>Code Narration Protocol</strong>
                  <p>
                    2-minute audio walkthrough before submission. Evaluated for clarity and logical
                    flow.
                  </p>
                </div>
                <div className="mechanic-item">
                  <strong>Teach-Back Loop</strong>
                  <p>
                    Explain a mastered concept to a peer who hasn't seen it. The teacher learns
                    twice {'\u2014'} the Prot{'\u00E9'}g{'\u00E9'} Effect.
                  </p>
                </div>
                <p className="mechanic-integration">
                  Reinforced by: Process-Based Assessment + Peer Learning
                </p>
              </div>
            </div>

            {/* Effective Interaction */}
            <div className={`competency-card ${reveal()}`}>
              <md-elevation />
              <div className="competency-card-header">
                <span className="material-symbols-outlined competency-icon competency-icon-interact">
                  group_work
                </span>
                <h3>Effective Interaction</h3>
                <span className="competency-badge competency-badge-interact">Core Competency</span>
              </div>
              <p className="competency-card-desc">
                Collaborate under structure. Navigate disagreement productively. Build shared
                understanding through protocol, not personality.
              </p>
              <div className="competency-mechanics">
                <div className="mechanic-item">
                  <strong>Paired Debugging Protocol</strong>
                  <p>
                    Driver/navigator roles swap every 15 minutes. The navigator articulates thinking
                    verbally {'\u2014'} cannot touch the keyboard.
                  </p>
                </div>
                <div className="mechanic-item">
                  <strong>Structured Code Review</strong>
                  <p>
                    Peer review with rubric: correctness, readability, naming, edge cases. Reviewing
                    is assessed as a competency.
                  </p>
                </div>
                <p className="mechanic-integration">
                  Reinforced by: Human Mentors + 1:1 Live Sessions
                </p>
              </div>
            </div>
          </div>

          {/* Facilitator Paradigm Callout */}
          <div className={`facilitator-callout ${reveal()}`}>
            <md-elevation />
            <div className="facilitator-header">
              <span className="material-symbols-outlined">school</span>
              <h3>The Facilitator Paradigm</h3>
            </div>
            <p className="facilitator-thesis">
              Our instructors are facilitators {'\u2014'} not broadcasters. Every session is designed
              for cognitive engagement at all times. Students construct knowledge; facilitators
              create the conditions.
            </p>
            <div className="facilitator-contrast">
              <div className="contrast-column contrast-old">
                <h4>Traditional Instructor</h4>
                <ul>
                  <li>Lectures for 60 minutes</li>
                  <li>Students take notes passively</li>
                  <li>Q&amp;A at the end (if time permits)</li>
                  <li>Assessment: {'\u201C'}Did you memorize this?{'\u201D'}</li>
                </ul>
              </div>
              <div className="contrast-divider">
                <span className="material-symbols-outlined">compare_arrows</span>
              </div>
              <div className="contrast-column contrast-ours">
                <h4>Our Facilitator</h4>
                <ul>
                  <li>Pre-work ensures readiness before session</li>
                  <li>Session is 100% application and debate</li>
                  <li>Facilitator observes, probes, redirects</li>
                  <li>Assessment: {'\u201C'}Can you defend your reasoning?{'\u201D'}</li>
                </ul>
              </div>
            </div>
            <p className="facilitator-summary">
              The goal is not to transfer knowledge {'\u2014'} it is to construct it. Facilitators
              design the struggle. Students own the outcome.
            </p>
          </div>

          <p className={`competency-manifesto ${reveal()}`}>
            We do not just teach code. We build thinkers.
          </p>
        </div>
      </section>
    </>
  );
}
